import {
  Delete as DeleteIcon,
  FilterList as FilterListIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import {
  Alert,
  alpha,
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  TablePagination,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../../contexts/AccountContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { getClients } from '../../services/adminService';
import { deleteUser, getUsers, revokeInvitation } from '../../services/api';
import { getTeamsByClient } from '../../services/teamService';
import { PERMISSIONS } from '../../shared/constants/permissions';
import type { UserRole } from '../../types';
import type { UserLimits } from '../../types/user';
import { type Column, StandardTable } from '../common/UI/StandardTable';
import ConfirmationDialog from '../Dialogs/ConfirmationDialog';
import InviteUserDialog from './InviteUserDialog';

const EMPTY_ARRAY: never[] = [];

type DisplayUser = {
  uid: string;
  displayName?: string;
  email?: string;
  role?: UserRole;
  status?: 'active' | 'pending' | 'inactive';
  lastLogin?: string;
  clientId?: string;
  teamId?: string;
  disabled?: boolean;
  limits?: UserLimits;
};

const getUsageChipColor = (
  currentCount: number | null,
  maxCount: number | null
): 'success' | 'warning' | 'error' | 'default' => {
  if (currentCount === null || maxCount === null) {
    return 'default';
  }
  const usage = currentCount / maxCount;
  if (usage >= 1) {
    return 'error';
  }
  if (usage >= 0.8) {
    return 'warning';
  }
  return 'success';
};

interface EnhancedTableToolbarProps {
  numSelected: number;
  onInviteUser: () => void;
  onDeleteUsers: () => void;
  currentUserCount: number | null;
  userLimit: number | null;
  canInviteUser: boolean;
  canDeleteUser: boolean;
}

const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
  const {
    numSelected,
    onInviteUser,
    onDeleteUsers,
    currentUserCount,
    userLimit,
    canInviteUser,
    canDeleteUser,
  } = props;

  const isFull = currentUserCount !== null && userLimit !== null && currentUserCount >= userLimit;
  const remainingInvites =
    userLimit !== null && currentUserCount !== null
      ? Math.max(0, userLimit - currentUserCount)
      : null;
  const chipColor = getUsageChipColor(currentUserCount, userLimit);

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
        }),
      }}
    >
      {numSelected > 0 ? (
        <Typography sx={{ flex: '1 1 100%' }} color="inherit" variant="subtitle1" component="div">
          {numSelected} selected
        </Typography>
      ) : (
        <Box
          sx={{ flex: '1 1 100%', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}
        >
          <Typography variant="h6" id="tableTitle" component="div">
            Users
          </Typography>
          {userLimit !== null && currentUserCount !== null && (
            <>
              <Chip
                label={`Users: ${currentUserCount} / ${userLimit}`}
                color={chipColor}
                sx={{ mr: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                ({remainingInvites} invites remaining)
              </Typography>
            </>
          )}
        </Box>
      )}

      {numSelected > 0 ? (
        canDeleteUser && (
          <Tooltip title="Delete">
            <IconButton onClick={onDeleteUsers} aria-label="delete users">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )
      ) : (
        <>
          {canInviteUser && (
            <Tooltip title={isFull ? 'User limit reached' : 'Invite New User'}>
              <span>
                <Button
                  onClick={onInviteUser}
                  disabled={isFull}
                  data-testid="invite-user-button"
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  sx={{ whiteSpace: 'nowrap', minWidth: 'fit-content', ml: 2 }}
                >
                  Invite User
                </Button>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Filter list">
            <IconButton>
              <FilterListIcon />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Toolbar>
  );
};

interface UserManagementPanelProps {
  clientId?: string;
}

function UserManagementPanel({ clientId: propClientId }: UserManagementPanelProps) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [users, setUsers] = useState<DisplayUser[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [currentUserCount, setCurrentUserCount] = useState<number | null>(null);
  const [userLimit, setUserLimit] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [teamsMap, setTeamsMap] = useState<Record<string, string>>({});
  const [clientsMap, setClientsMap] = useState<Record<string, string>>({});

  const { account: currentUser, hasPermission } = useAccount();
  const { showSnackbar } = useSnackbar();

  const canManageSystem = hasPermission(PERMISSIONS.SYSTEM_MANAGE);
  const canInviteUser = hasPermission(PERMISSIONS.USER_INVITE);
  const canDeleteUser = hasPermission(PERMISSIONS.USER_DELETE);

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);

  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [confirmationConfig, setConfirmationConfig] = useState<{
    title: string;
    description: string;
    onConfirm?: () => void;
  }>({
    title: '',
    description: '',
  });

  const fetchTeams = useCallback(async () => {
    const targetClientId = propClientId || currentUser?.clientId;
    if (!targetClientId) {
      return;
    }

    try {
      const teams = await getTeamsByClient(targetClientId);
      const mapping: Record<string, string> = {};
      for (const team of teams) {
        mapping[team.id] = team.teamName;
      }
      setTeamsMap(mapping);
    } catch (_err) {
      // Fail silently
    }
  }, [propClientId, currentUser?.clientId]);

  const fetchClients = useCallback(async () => {
    if (!canManageSystem) {
      return;
    }
    try {
      const response = await getClients();
      if (response.success && response.clients) {
        const mapping: Record<string, string> = {};
        for (const client of response.clients) {
          if (client.id) {
            mapping[client.id] = client.name || client.id;
          }
        }
        setClientsMap(mapping);
      }
    } catch (_err) {
      // Fail silently
    }
  }, [canManageSystem]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: { page: number; limit: number; clientId?: string } = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (propClientId) {
        filters.clientId = propClientId;
      } else if (!canManageSystem && currentUser?.clientId) {
        filters.clientId = currentUser.clientId;
      }

      const response = await getUsers(filters);
      setUsers((response.users as DisplayUser[]) || EMPTY_ARRAY);
      setTotalUsers(response.total || 0);

      if (response.userLimit !== undefined) {
        setUserLimit(response.userLimit);
      }
      if (response.currentUserCount !== undefined) {
        setCurrentUserCount(response.currentUserCount);
      }
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message || 'Failed to fetch users.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, currentUser, propClientId, canManageSystem]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = users.map((n) => n.uid);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (_event: React.MouseEvent<unknown>, uid: string) => {
    const selectedIndex = selected.indexOf(uid);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, uid);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteUsers = () => {
    if (selected.length === 0) {
      return;
    }

    setConfirmationConfig({
      title: 'Delete User(s)',
      description: `Are you sure you want to delete ${selected.length} user(s)? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await Promise.all(
            selected.map((uid) => {
              const user = users.find((u) => u.uid === uid);
              if (user?.status === 'pending') {
                return revokeInvitation(uid);
              }
              return deleteUser(uid);
            })
          );
          showSnackbar('User(s) deleted successfully.', 'success');
          setSelected([]);
          fetchUsers();
        } catch (error: unknown) {
          const err = error as Error;
          showSnackbar(err.message || 'Failed to delete users.', 'error');
        }
        setConfirmationDialogOpen(false);
      },
    });

    setConfirmationDialogOpen(true);
  };

  const handleUserInvited = () => {
    showSnackbar('User invited successfully!', 'success');
    fetchUsers();
  };

  const isSelected = (uid: string) => selected.indexOf(uid) !== -1;

  const getStatusChip = (user: DisplayUser) => {
    if (user.status === 'pending') {
      return <Chip label="Pending" color="warning" />;
    }
    if (user.disabled) {
      return <Chip label="Inactive" color="default" />;
    }
    return <Chip label="Active" color="success" />;
  };

  const columns: Column<DisplayUser>[] = [
    {
      id: 'checkbox',
      label: (
        <Checkbox
          color="primary"
          indeterminate={selected.length > 0 && selected.length < users.length}
          checked={users.length > 0 && selected.length === users.length}
          onChange={handleSelectAllClick}
          inputProps={{ 'aria-label': 'select all users' }}
        />
      ),
      width: 48,
      render: (row) => (
        <Checkbox
          color="primary"
          checked={isSelected(row.uid)}
          onClick={(event) => {
            event.stopPropagation();
            handleClick(event, row.uid);
          }}
          inputProps={{ 'aria-labelledby': `enhanced-table-checkbox-${row.uid}` }}
        />
      ),
    },
    {
      id: 'displayName',
      label: 'Name',
      render: (row) => row.displayName || 'N/A',
    },
    {
      id: 'email',
      label: 'Email',
      render: (row) => row.email,
    },
    {
      id: 'role',
      label: 'Role',
      render: (row) => (row.status === 'pending' ? <Chip label={row.role} /> : row.role),
    },
    {
      id: 'teamId',
      label: 'Team',
      render: (row) => (row.teamId ? teamsMap[row.teamId] || 'Unknown Team' : '-'),
    },
    ...(canManageSystem
      ? [
          {
            id: 'clientId',
            label: 'Client',
            render: (row: DisplayUser) =>
              row.clientId ? clientsMap[row.clientId] || row.clientId : 'N/A',
          } as Column<DisplayUser>,
        ]
      : []),
    {
      id: 'status',
      label: 'Status',
      render: (row) => getStatusChip(row),
    },
    {
      id: 'lastLogin',
      label: 'Last Login',
      render: (row) => (row.lastLogin ? new Date(row.lastLogin).toLocaleString() : 'Never'),
    },
  ];

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <>
      <Box sx={{ width: '100%', mb: 2 }}>
        <EnhancedTableToolbar
          numSelected={selected.length}
          onInviteUser={() => setInviteDialogOpen(true)}
          onDeleteUsers={handleDeleteUsers}
          currentUserCount={currentUserCount}
          userLimit={userLimit}
          canInviteUser={canInviteUser}
          canDeleteUser={canDeleteUser}
        />
        <StandardTable
          columns={columns}
          data={users}
          keyExtractor={(row) => row.uid}
          isLoading={loading}
          emptyStateMessage="No users found."
          onRowClick={(row) => navigate(`/admin/user/${row.uid}`)}
          rowSx={() => ({
            '&:nth-of-type(odd)': { backgroundColor: 'action.hover' },
            '&:hover': { backgroundColor: 'action.selected' },
          })}
          size="medium"
        />
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalUsers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
      <InviteUserDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        onUserInvited={handleUserInvited}
        {...(propClientId ? { companyId: propClientId } : {})}
      />
      <ConfirmationDialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        onConfirm={
          confirmationConfig.onConfirm ||
          (() => {
            // No-op fallback
          })
        }
        title={confirmationConfig.title}
        contentText={confirmationConfig.description}
      />
    </>
  );
}

export default UserManagementPanel;
